import { createSelector } from '@reduxjs/toolkit';
import { toIQuantity } from 'Data/ViewModels/Core/IQuantity';
import { isEmpty, mergeRight } from 'ramda';
import * as oPath from '../../../../Lib/Object/Path/Immutable';

const getValue = (value) => {
  if (value && value.uom) return value.value; // Extract value from unit model
  if (value === undefined) return null; // Remove undefined

  return value;
};

const getMessagesForPath = (equipment, path) => {
  let pathArray = path.split('.');

  const propName = pathArray.pop();

  pathArray.push('Messages');
  const messagesData = oPath.get(equipment, pathArray.join('.'));

  const messages =
    messagesData &&
    messagesData
      .filter((m) => m.PropertyName === propName)
      .map((m) => ({ message: m.Text, severity: m.MessageType.toLowerCase() }));

  if (messages) return messages;
  else return null;
};

const getConfigValue = (config, name) => {
  if (!config || !name) return null;
  if (typeof name !== 'string') return null;

  // Try to find a config for the exact name.
  if (config[name]) return config[name];

  // If there isn't one, remove array indices from the top level down to look for a generalised config.
  var arrayIndexRegex = /\[[0-9]*\]/;
  while (arrayIndexRegex.test(name)) {
    name = name.replace(arrayIndexRegex, '');
    if (config[name]) return config[name];
  }

  return null;
};

const equipmentSelector = (state) => state.equipment?.syncModel;

const valueSelector = (name) =>
  createSelector(equipmentSelector, (equipment) => ({ value: getValue(oPath.get(equipment, name)) }));

const tempValueSelector = (name, tempStateId) =>
  createSelector(
    (state) => state.equipment?.equipmentTemp && state.equipment.equipmentTemp[tempStateId],
    valueSelector(name),
    (equipmentTempValues, equipmentValue) => {
      let value =
        (equipmentTempValues && equipmentTempValues?.find((kvp) => kvp.key === name)?.value) || equipmentValue;
      return { value: getValue(value) };
    }
  );

const configSelector = (name) =>
  createSelector(
    equipmentSelector,
    (state) => state.equipment?.equipmentType,
    (state) => state.equipment?.config,
    (equipment, equipmentType, config) => {
      let configProps = (name && getConfigValue(config, name)) || {};
      if (typeof configProps === 'function') {
        configProps = configProps({ name, equipment, equipmentType });
      }
      if (typeof configProps.format === 'function') {
        configProps = {
          ...configProps,
          format: configProps.format.bind(null, { name, equipment, equipmentType }),
        };
      }
      return configProps;
    }
  );

const uomSelector = (configSelector, valueSelector, consumerUom) =>
  createSelector(configSelector, valueSelector, (config, value) => config?.uom || consumerUom || value?.uom);

const labelSelector = (configSelector, uomSelector, consumerLabel) =>
  createSelector(configSelector, uomSelector, (config, uom) => {
    let label = config?.label || consumerLabel;
    label = label && uom ? `${label} (${uom})` : label;
    return { label };
  });

const messagesSelector = (name) =>
  createSelector(equipmentSelector, (equipment) => ({ messages: getMessagesForPath(equipment, name) }));

const onChangeWithUomSelector = (uomSelector, onChange) =>
  createSelector(uomSelector, (uom) => {
    return { onChange: onChangeWithUom(onChange, uom) };
  });

const onChangeWithUom = (onChange, uom) => (evt) => {
  if (uom && evt.data && evt.data.value !== undefined) {
    // Quantity fields have a UOM, so the value they emit should be a bit different to other fields. Doing it here because
    // individual inputs shouldn't really need to know about quantities, they just emit a value. It's also convenient to
    // do here because we have the config property 'uom'.
    evt.data.value = toIQuantity(evt.data.value, uom);
  }
  onChange(evt);
};

// Only props you want to override on the consumer need to be returned.
const getCommonProps = (consumerProps, providerData) => {
  let {
    alwaysEnabled,
    label: consumerLabel,
    name,
    namePrefix,
    prefixName: consumerPrefixName,
    uom: consumerUom,
    messages: consumerMessages,
    selectors: consumerSelectors,
  } = consumerProps;
  let { equipmentId, locked } = providerData;

  // Create a default prefixName that doesn't modify the name
  let prefixName = consumerPrefixName || ((n) => n);

  name = name && prefixName(name);

  let providerProps = { namePrefix, prefixName, equipmentId, equipmentSelector, hasInjectedProps: true };

  // Set disabled
  if (!alwaysEnabled && locked) {
    providerProps.disabled = true;
  }

  providerProps.selectors = consumerSelectors;
  if (name) {
    providerProps.selectors = providerProps.selectors ?? {};
    providerProps.selectors.config = configSelector(name);
    providerProps.selectors.label = labelSelector(
      configSelector(name),
      uomSelector(configSelector(name), valueSelector(name), consumerUom),
      consumerLabel
    );
    if (consumerMessages === undefined) {
      providerProps.selectors.messages = messagesSelector(name);
    }
  }

  return providerProps;
};

const getEquipmentSpecificProps = (consumerProps, providerData) => {
  let {
    name,
    prefixName: consumerPrefixName,
    onChange: consumerOnChange,
    value: consumerValue,
    uom: consumerUom,
    copyTo,
  } = consumerProps;
  let { setValue, synchronise } = providerData;

  // Create a default prefixName that doesn't modify the name
  let prefixName = consumerPrefixName || ((n) => n);

  name = name && prefixName(name);

  let providerProps = {};

  // Value selector
  if (name && consumerValue === undefined) {
    if (!providerProps.selectors) providerProps.selectors = {};
    providerProps.selectors.value = valueSelector(name);
  }

  // onChange
  if (name && !consumerOnChange) {
    /*
     * There is a name in the event from the DOM element itself. We want the name from the consumer props
     * instead because we intercept and modify it for measured tests (and maybe other cases in the future).
     */
    const onChange = ({ data: { value, submitValues = false } = {} }) => {
      if (name !== undefined && value !== undefined) {
        setValue({ name, value });
        copyTo?.forEach((d) => setValue({ name: prefixName(d), value }));
      }
      if (submitValues) synchronise();
    };

    // Add selector for onChangeWithUom that will select the correct uom
    if (!providerProps.selectors) providerProps.selectors = {};
    providerProps.selectors.onChange = onChangeWithUomSelector(
      uomSelector(configSelector(name), valueSelector(name), consumerUom),
      onChange
    );
  }

  return providerProps;
};

// Only props you want to override on the consumer need to be returned.
const getEquipmentTempSpecificProps = (consumerProps, providerData) => {
  let { name, onChange: consumerOnChange, value: consumerValue, uom: consumerUom } = consumerProps;
  let { tempStateId, setTempStateValue } = providerData;
  // Create a default prefixName that doesn't modify the name

  let providerProps = {};

  // Value selector
  if (name && consumerValue === undefined) {
    if (!providerProps.selectors) providerProps.selectors = {};
    providerProps.selectors.value = tempValueSelector(name, tempStateId);
  }

  // Set onChange
  if (name && !consumerOnChange) {
    const onChange = ({ data: { value } = {} }) => {
      /*
       * There is a name in the data object from the DOM element itself. We want the name from the consumer props
       * instead because we intercept and modify it for embedded tests (and maybe other cases in the future).
       */
      setTempStateValue({ tempId: tempStateId, key: name, value });
    };

    // Add selector for onChangeWithUom that will select the correct uom
    if (!providerProps.selectors) providerProps.selectors = {};
    providerProps.selectors.onChange = onChangeWithUomSelector(
      uomSelector(configSelector(name), tempValueSelector(name, tempStateId), consumerUom),
      onChange
    );
  }

  return providerProps;
};

export const getEquipmentProviderProps = (consumerProps, providerData, modifiers = []) => {
  // The below may be useful again one day if we can find a way to keep the worksheetId conditional.
  //    If props need to be overwritten then return a dict of props to be overwritten otherwise return null.
  //    return Object.keys(providerProps).length > 0 ? providerProps : null;

  consumerProps = { ...consumerProps };
  providerData = { ...providerData };
  modifiers.forEach((m) => m(consumerProps, providerData));

  return mergeProps(
    consumerProps,
    getCommonProps(consumerProps, providerData),
    getEquipmentSpecificProps(consumerProps, providerData)
  );
};

// Merge objects containing props, combining any special "additive" properties (like selectors)
const mergeProps = (...propObjects) => {
  let props = {};
  let selectors = {};

  propObjects.forEach(({ selectors: pSelectors, ...pProps }) => {
    props = mergeRight(props, pProps);
    selectors = mergeRight(selectors, pSelectors);
  });

  if (!isEmpty(selectors)) return { ...props, selectors };

  return props;
};

export const getEquipmentTempProviderProps = (consumerProps, providerData, modifiers = []) => {
  consumerProps = { ...consumerProps };
  providerData = { ...providerData };
  modifiers.forEach((m) => m(consumerProps, providerData));
  return mergeProps(
    getCommonProps(consumerProps, providerData),
    getEquipmentTempSpecificProps(consumerProps, providerData)
  );
};
